import React from 'react'
import { Address } from '../../../types/api'
import { colors, typography, spacing, borderRadius, shadows } from '../../../styles/design-system'

interface AccountAddressesTabProps {
  addresses: Address[]
  onNewAddress: () => void
  onEditAddress: (address: Address) => void
  onDeleteAddress: (id: string) => void
  onSetDefaultAddress: (id: string) => void
  isLoading: boolean
  deletingAddressId?: string
  updatingAddressId?: string
  settingDefaultAddressId?: string
  isMobile?: boolean
}

const AccountAddressesTab: React.FC<AccountAddressesTabProps> = ({
  addresses,
  onNewAddress,
  onEditAddress,
  onDeleteAddress,
  onSetDefaultAddress,
  isLoading,
  deletingAddressId,
  updatingAddressId,
  settingDefaultAddressId,
  isMobile = false
}) => {
  const isBusy = (id: string, type: 'delete' | 'update' | 'default') => {
    if (type === 'delete') {
      return deletingAddressId === id
    }
    if (type === 'update') {
      return updatingAddressId === id
    }
    return settingDefaultAddressId === id
  }

  return (
    <div
      style={{
        backgroundColor: colors.neutral.white,
        borderRadius: borderRadius.xl,
        boxShadow: shadows.sm,
        padding: isMobile ? spacing.lg : spacing['2xl']
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between',
          alignItems: isMobile ? 'stretch' : 'center',
          gap: isMobile ? spacing.md : '0',
          marginBottom: spacing.xl
        }}
      >
        <h2
          style={{
            fontSize: typography.fontSize['2xl'],
            fontWeight: typography.fontWeight.bold,
            color: colors.neutral.gray900,
            margin: 0
          }}
        >
          Meus Endereços
        </h2>

        <button
          onClick={onNewAddress}
          style={{
            backgroundColor: colors.primary.blue,
            color: colors.neutral.white,
            border: 'none',
            padding: `${spacing.md} ${spacing.lg}`,
            borderRadius: borderRadius.lg,
            fontSize: isMobile ? typography.fontSize.sm : typography.fontSize.base,
            fontWeight: typography.fontWeight.semibold,
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            boxShadow: shadows.sm,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: spacing.sm,
            width: isMobile ? '100%' : 'auto'
          }}
          onMouseEnter={(event) => {
            event.currentTarget.style.backgroundColor = colors.secondary.darkBlue
            event.currentTarget.style.transform = 'translateY(-2px)'
            event.currentTarget.style.boxShadow = shadows.md
          }}
          onMouseLeave={(event) => {
            event.currentTarget.style.backgroundColor = colors.primary.blue
            event.currentTarget.style.transform = 'translateY(0)'
            event.currentTarget.style.boxShadow = shadows.sm
          }}
        >
          <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Novo Endereço
        </button>
      </div>

      {isLoading ? (
        <p
          style={{
            color: colors.neutral.gray600,
            fontSize: typography.fontSize.base
          }}
        >
          Carregando endereços...
        </p>
      ) : addresses.length === 0 ? (
        <div
          style={{
            border: `1px dashed ${colors.neutral.gray300}`,
            borderRadius: borderRadius.xl,
            padding: spacing.xl,
            backgroundColor: colors.neutral.gray50,
            textAlign: 'center'
          }}
        >
          <p
            style={{
              color: colors.neutral.gray600,
              fontSize: typography.fontSize.base,
              marginBottom: spacing.lg
            }}
          >
            Você ainda não cadastrou nenhum endereço.
          </p>
          <button
            onClick={onNewAddress}
            style={{
              backgroundColor: colors.primary.blue,
              color: colors.neutral.white,
              border: 'none',
              padding: `${spacing.sm} ${spacing.lg}`,
              borderRadius: borderRadius.lg,
              fontSize: typography.fontSize.sm,
              fontWeight: typography.fontWeight.semibold,
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              boxShadow: shadows.sm,
              display: 'inline-flex',
              alignItems: 'center',
              gap: spacing.xs
            }}
            onMouseEnter={(event) => {
              event.currentTarget.style.backgroundColor = colors.secondary.darkBlue
              event.currentTarget.style.transform = 'translateY(-2px)'
              event.currentTarget.style.boxShadow = shadows.md
            }}
            onMouseLeave={(event) => {
              event.currentTarget.style.backgroundColor = colors.primary.blue
              event.currentTarget.style.transform = 'translateY(0)'
              event.currentTarget.style.boxShadow = shadows.sm
            }}
          >
            <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Cadastrar Endereço
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.lg }}>
          {addresses.map((address, index) => {
            const addressId = address.id ?? `address-${index}`
            const isPersisted = Boolean(address.id)
            const busyDefault = isPersisted ? isBusy(address.id!, 'default') : false
            const busyUpdate = isPersisted ? isBusy(address.id!, 'update') : false
            const busyDelete = isPersisted ? isBusy(address.id!, 'delete') : false
            return (
              <div
                key={addressId}
                style={{
                  border: `1px solid ${colors.neutral.gray200}`,
                  borderRadius: borderRadius.xl,
                  padding: spacing.xl,
                  backgroundColor: colors.neutral.gray50,
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(event) => {
                  event.currentTarget.style.borderColor = colors.primary.blue
                  event.currentTarget.style.backgroundColor = colors.secondary.lightBlue
                }}
                onMouseLeave={(event) => {
                  event.currentTarget.style.borderColor = colors.neutral.gray200
                  event.currentTarget.style.backgroundColor = colors.neutral.gray50
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    flexDirection: isMobile ? 'column' : 'row',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    gap: isMobile ? spacing.md : '0',
                    marginBottom: spacing.sm
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: spacing.md,
                      flex: 1,
                      width: isMobile ? '100%' : 'auto'
                    }}
                  >
                    <div
                      aria-hidden
                      style={{
                        backgroundColor: colors.secondary.lightBlue,
                        borderRadius: borderRadius.full,
                        width: '48px',
                        height: '48px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        marginTop: spacing.xs
                      }}
                    >
                      <svg width="24" height="24" fill="none" stroke={colors.primary.blue} viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </div>

                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: spacing.sm,
                          marginBottom: spacing.xs
                        }}
                      >
                        <h3
                          style={{
                            fontSize: typography.fontSize.lg,
                            fontWeight: typography.fontWeight.semibold,
                            color: colors.neutral.gray900,
                            margin: 0
                          }}
                        >
                          {address.name}
                        </h3>
                        {address.isDefault && (
                          <span
                            style={{
                              backgroundColor: colors.status.success,
                              color: colors.neutral.white,
                              padding: `${spacing.xs} ${spacing.sm}`,
                              borderRadius: borderRadius.md,
                              fontSize: typography.fontSize.xs,
                              fontWeight: typography.fontWeight.semibold,
                              letterSpacing: '0.025em'
                            }}
                          >
                            Padrão
                          </span>
                        )}
                      </div>
                      <p
                        style={{
                          color: colors.neutral.gray600,
                          margin: 0,
                          lineHeight: typography.lineHeight.relaxed,
                          fontSize: typography.fontSize.sm
                        }}
                      >
                        {address.street}, {address.number}
                        {address.complement ? `, ${address.complement}` : ''}
                        <br />
                        {address.neighborhood} - {address.city}/{address.state}
                        <br />
                        CEP: {address.zipCode}
                      </p>
                    </div>
                  </div>

                  <div
                    style={{
                      display: 'flex',
                      flexDirection: isMobile ? 'column' : 'row',
                      gap: spacing.sm,
                      width: isMobile ? '100%' : 'auto'
                    }}
                  >
                    {isPersisted && !address.isDefault && (
                      <button
                        onClick={() => onSetDefaultAddress(address.id!)}
                        style={{
                          backgroundColor: colors.status.success,
                          color: colors.neutral.white,
                          border: 'none',
                          padding: `${spacing.sm} ${spacing.md}`,
                          borderRadius: borderRadius.lg,
                          fontSize: typography.fontSize.sm,
                          fontWeight: typography.fontWeight.medium,
                          cursor: busyDefault ? 'wait' : 'pointer',
                          transition: 'all 0.2s ease',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: spacing.xs,
                          opacity: busyDefault ? 0.7 : 1,
                          width: isMobile ? '100%' : 'auto'
                        }}
                        onMouseEnter={(event) => {
                          if (!busyDefault) {
                            event.currentTarget.style.transform = 'translateY(-2px)'
                            event.currentTarget.style.boxShadow = shadows.sm
                          }
                        }}
                        onMouseLeave={(event) => {
                          event.currentTarget.style.transform = 'translateY(0)'
                          event.currentTarget.style.boxShadow = 'none'
                        }}
                        disabled={busyDefault}
                      >
                        <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        {busyDefault ? 'Definindo...' : 'Definir Padrão'}
                      </button>
                    )}
                    <button
                      onClick={() => onEditAddress(address)}
                      style={{
                        backgroundColor: colors.primary.blue,
                        color: colors.neutral.white,
                        border: 'none',
                        padding: `${spacing.sm} ${spacing.md}`,
                        borderRadius: borderRadius.lg,
                        fontSize: typography.fontSize.sm,
                        fontWeight: typography.fontWeight.medium,
                        cursor: busyUpdate ? 'wait' : 'pointer',
                        transition: 'all 0.2s ease',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: spacing.xs,
                        opacity: isPersisted ? (busyUpdate ? 0.7 : 1) : 1,
                        width: isMobile ? '100%' : 'auto'
                      }}
                      onMouseEnter={(event) => {
                        if (!busyUpdate) {
                          event.currentTarget.style.transform = 'translateY(-2px)'
                          event.currentTarget.style.boxShadow = shadows.sm
                        }
                      }}
                      onMouseLeave={(event) => {
                        event.currentTarget.style.transform = 'translateY(0)'
                        event.currentTarget.style.boxShadow = 'none'
                      }}
                      disabled={isPersisted ? busyUpdate : true}
                    >
                      <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                      Editar
                    </button>
                    <button
                      onClick={() => isPersisted && onDeleteAddress(address.id!)}
                      style={{
                        backgroundColor: colors.status.error,
                        color: colors.neutral.white,
                        border: 'none',
                        padding: `${spacing.sm} ${spacing.md}`,
                        borderRadius: borderRadius.lg,
                        fontSize: typography.fontSize.sm,
                        fontWeight: typography.fontWeight.medium,
                        cursor: busyDelete ? 'wait' : 'pointer',
                        transition: 'all 0.2s ease',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: spacing.xs,
                        opacity: isPersisted ? (busyDelete ? 0.7 : 1) : 0.6,
                        width: isMobile ? '100%' : 'auto'
                      }}
                      onMouseEnter={(event) => {
                        if (!busyDelete) {
                          event.currentTarget.style.transform = 'translateY(-2px)'
                          event.currentTarget.style.boxShadow = shadows.sm
                        }
                      }}
                      onMouseLeave={(event) => {
                        event.currentTarget.style.transform = 'translateY(0)'
                        event.currentTarget.style.boxShadow = 'none'
                      }}
                      disabled={!isPersisted || busyDelete}
                    >
                      <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      {isPersisted ? (busyDelete ? 'Removendo...' : 'Excluir') : 'Indisponível'}
                    </button>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default AccountAddressesTab

